import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PanelLeft, Loader2, X, Menu } from 'lucide-react';

import { Sidebar } from '@/components/Sidebar';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { MeshFilesProvider } from '@/contexts/MeshFilesContext';
import { FloatingAuthModal } from '@/components/auth/FloatingAuthModal';
import { PostHogProvider } from '@/contexts/PostHogProvider';

export default function App() {
  const { isLoading } = useAuth();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    localStorage.getItem('sidebarOpen') !== 'false',
  );
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebarOpen', isSidebarOpen.toString());
  }, [isSidebarOpen]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-adam-bg-secondary-dark">
        <Loader2 className="h-8 w-8 animate-spin text-adam-blue" />
      </div>
    );
  }

  return (
    <PostHogProvider>
      <div className="h-dvh overflow-hidden">
        {isMobile ? (
          // Mobile Layout
          <div className="flex h-dvh flex-col">
            {/* Mobile Header - hidden for editor pages (they render their own) */}
            {!location.pathname.startsWith('/editor/') && (
              <header className="flex shrink-0 items-center justify-between border-b border-adam-neutral-700 bg-adam-bg-dark px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-adam-text-primary"
                  onClick={() => setIsMobileMenuOpen(true)}
                >
                  <Menu className="h-5 w-5" />
                </Button>
                <img
                  src="/adam-logo-full.svg"
                  alt="CADAM"
                  className="h-6"
                />
                <div className="w-9" /> {/* Spacer for centering */}
              </header>
            )}

            {/* Mobile Menu Overlay */}
            {isMobileMenuOpen && (
              <div
                className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div
                  className="absolute left-0 top-0 h-full w-72 bg-adam-bg-dark shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex h-14 items-center justify-between border-b border-adam-neutral-700 px-4">
                    <span className="text-lg font-semibold text-adam-text-primary">Menu</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-adam-text-primary"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  <Sidebar isSidebarOpen={true} />
                </div>
              </div>
            )}

            {/* Mobile Content */}
            <div className="flex-1 overflow-auto bg-adam-bg-dark">
              <MeshFilesProvider>
                <Outlet context={{ isSidebarOpen: false, isMobile: true, openMobileMenu: () => setIsMobileMenuOpen(true) }} />
              </MeshFilesProvider>
            </div>

            {/* Floating auth modal */}
            <FloatingAuthModal />
          </div>
        ) : (
          // Desktop Layout
          <div className="flex h-dvh transition-all ease-in-out">
            <Sidebar isSidebarOpen={isSidebarOpen} />
            <div className="relative flex-1 overflow-auto bg-adam-bg-dark">
              {/* Toggle Sidebar Button */}
              <Button
                variant="ghost"
                size="icon"
                className={`bg-adam-neutral-3000 fixed z-10 h-7 w-7 rounded-md text-gray-400 transition-all duration-300 [@media(hover:hover)]:hover:bg-adam-neutral-950 [@media(hover:hover)]:hover:text-adam-neutral-10 ${
                  isSidebarOpen ? 'left-[272px]' : 'left-20'
                } ${
                  location.pathname === '/' && isSidebarOpen
                    ? 'top-[2.25rem]'
                    : 'top-3.5'
                }`}
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              >
                <PanelLeft className="h-4 w-4" />
              </Button>
              <div className="h-full bg-adam-bg-dark">
                <MeshFilesProvider>
                  <Outlet context={{ isSidebarOpen, isMobile: false }} />
                </MeshFilesProvider>
              </div>
            </div>
            <FloatingAuthModal />
          </div>
        )}
      </div>
    </PostHogProvider>
  );
}
